import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';

import { translate, translateObject } from '@jsverse/transloco';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { AnalyticsService } from 'app/providers/analytics.service';
import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import {
  AnalysisGroup,
  AnalysisGroupService,
} from 'app/providers/storage-api/analysis-group-service';
import { Analysis } from 'app/providers/storage-api/analysis.service';
import { Project } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { SystemService } from '../../../../services/system.service';
import { ProjectCollection } from '../../models/project-collection.model';
import { ProjectDetailService } from '../../services/project-detail.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-group-actions',
  templateUrl: './project-group-actions.component.html',
  styleUrls: ['./project-group-actions.component.scss'],
  standalone: false,
})
export class ProjectGroupActionsComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() project: Project;
  @Input() configurations: Map<string, SemantriaConfiguration>;
  @Input() projectCollections: ProjectCollection[];

  groups: AnalysisGroup[];

  showRenameAnalysisGroupModal = false;
  currentAnalysisGroup: AnalysisGroup;

  showNewAnalysisGroupModal = false;
  newAnalysisGroupModalData: {
    configurations: Map<string, SemantriaConfiguration>;
    project: Project;
    collections: ProjectCollection[];
    analysisGroup: AnalysisGroup;
  };
  private localStorageService = inject(LocalStorageService);

  constructor(
    private alertService: AlertService,
    private dialogService: DialogService,
    private googleAnalyticsService: AnalyticsService,
    private projectDetailService: ProjectDetailService,
    private analysisGroupService: AnalysisGroupService,
    public dialog: MatDialog,
    private systemService: SystemService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.project && changes.project.currentValue) {
      this.groups = this.project.groups;
    }
  }

  public onShowNewAnalysisGroupDialog(analysisGroup?: AnalysisGroup) {
    this.newAnalysisGroupModalData = {
      configurations: this.configurations,
      project: this.project,
      collections: this.projectCollections,
      analysisGroup,
    };
    this.showNewAnalysisGroupModal = true;
  }

  onNewAnalysisGroupDialogClosed(result: AnalysisGroup) {
    this.showNewAnalysisGroupModal = false;
    if (result) {
      this.alertService.showToast(
        this.newAnalysisGroupModalData.analysisGroup
          ? translate('project-group-actions.group-updated', { name: result.name })
          : translate('project-group-actions.group-created', { name: result.name })
      );
      this.projectDetailService.projectRefreshNeeded();
      this.googleAnalyticsService.sendEvent('create_analysisGroup', 'Projects');
    }
  }

  public showAnalysisGroupDeleteDialog(analysisGroup: AnalysisGroup) {
    const translations = translateObject('project-group-actions.delete-group', {
      title: {
        name: analysisGroup.name,
      },
      deleting: {
        name: analysisGroup.name,
      },
    });

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.alertService.showToast(translations['deleting']);
          try {
            await this.analysisGroupService.deleteAnalysisGroup(this.project.id, analysisGroup.id);
            const idx = this.groups.findIndex(function (d) {
              return d.id === analysisGroup.id;
            });
            if (idx !== -1) {
              this.groups.splice(idx, 1);
            }
            this.projectDetailService.projectRefreshNeeded();
            this.googleAnalyticsService.sendEvent('delete_analysisGroup', 'Projects');
          } catch (error) {
            this.alertService.showErrorToast(translations['delete-failed'], error);
            this.googleAnalyticsService.sendEvent('delete_analysisGroup_failed', 'Projects');
          }
        }
      });
  }

  public async onRemoveAnalysisFromGroup(analysisGroup: AnalysisGroup, analysis: Analysis) {
    try {
      await this.analysisGroupService.removeAnalysesFromAnalysisGroup(
        this.project.id,
        analysisGroup.id,
        [analysis.analysisName]
      );
      this.alertService.showToast(
        translate('project-group-actions.analysis-removed', {
          displayName: analysis.displayName,
          groupName: analysisGroup.name,
        })
      );
    } catch (error) {
      this.alertService.showErrorToast(
        translate('project-group-actions.failed-analysis-removed', {
          displayName: analysis.displayName,
          groupName: analysisGroup.name,
        }),
        error
      );
    }
  }

  public onShowRenameAnalysisGroupDialog(analysisGroup: AnalysisGroup) {
    this.currentAnalysisGroup = analysisGroup;
    this.showRenameAnalysisGroupModal = true;
  }

  public async onRenameAnalysisGroupDialogClosed(analysisGroup: AnalysisGroup) {
    this.showRenameAnalysisGroupModal = false;
    if (analysisGroup) {
      try {
        await this.analysisGroupService.updateAnalysisGroup(this.project.id, analysisGroup.id, {
          name: analysisGroup.name,
          lookupId: analysisGroup.lookupId,
        });
        this.alertService.showToast(translate('project-group-actions.name-saved'));
        this.projectDetailService.projectRefreshNeeded();
      } catch (e) {
        this.alertService.showErrorToast(translate('project-group-actions.failed-name-saved'), e);
      }
    }
  }

  async cloneAnalysisGroup(analysisGroup: AnalysisGroup) {
    if (this.groups.find((g) => g.name === analysisGroup.name + '_Copy')) {
      this.alertService.showToast(
        translate('project-group-actions.clone-name-in-use', { name: analysisGroup.name })
      );
      return;
    }

    try {
      const clone = await this.analysisGroupService.cloneAnalysisGroup(
        this.project.id,
        analysisGroup.id
      );

      const prevSelectedItem = this.localStorageService.getItem('lastProjectItemSelected');
      const newSelectedItem = {
        itemId: clone.id,
        projectId: this.project.id,
        index: null,
        sortDir: prevSelectedItem.sortDir,
        sortProp: prevSelectedItem.sortProp,
      };

      this.localStorageService.setItem('lastProjectItemSelected', newSelectedItem);
      this.alertService.showToast(
        translate('project-group-actions.clone-success', { name: clone.name })
      );
      this.projectDetailService.projectRefreshNeeded();
      this.googleAnalyticsService.sendEvent('clone_analysisGroup', 'Projects');
    } catch (e) {
      this.alertService.showErrorToast(translate('project-group-actions.clone-failed'), e);
    }
  }

  public get hasAnalyses() {
    if (!this.project) {
      return false;
    }
    return (
      this.project.collections.filter(
        (collection) => collection.analysisId || collection.analysisCount > 0
      ).length > 0
    );
  }

  onCopyAnalysisGroupIdToClipboard(ag: AnalysisGroup) {
    this.systemService.copyToClipboard(ag.id);
    this.alertService.showToast(translate('project-collection-actions.analysis-group-id-copied'));
  }
}
